import type { UnknownQueryParam, Url } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { RawAccountTypeDetailsMap } from "~/areas/infrastructure/InfrastructureDetails";
import { client } from "~/clientInstance";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import type { UrlResolver } from "~/components/Navigation/useUrlResolver";
import type { PermissionCheckProps } from "~/components/PermissionCheck/PermissionCheck";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import type { PageRegistration } from "~/routing/pageRegistrations/PageRegistration";
import { allPages } from "~/routing/pageRegistrations/allPageRegistrations";
export interface GlobalSearchPageRegistration {
    Id(urlResolver: UrlResolver): string;
    name?: string;
    area: string;
    areaAndKeywords: string;
    isAuthorized(): boolean;
    resolvedUrl(urlResolver: UrlResolver): string;
}
export function createGlobalSearchPageRegistration<RouteParams, QueryParams extends UnknownQueryParam[], LoaderData, LoaderContext, PageContext>({ RouteLink, pageRegistration, label, }: {
    pageRegistration: PageRegistration<RouteParams, QueryParams, LoaderData, LoaderContext, PageContext>;
    label: string;
    RouteLink: () => Url;
    RoutePermission?: PermissionCheckProps;
}): GlobalSearchPageRegistration {
    const pageArea = getPageArea(pageRegistration.pageArea);
    return {
        Id(urlResolver: UrlResolver) {
            // Assume links are unique across global search. Every page we link to should have a single entry in global search.
            return urlResolver.resolve(RouteLink());
        },
        areaAndKeywords: pageRegistration.searchKeywords.length > 0 ? `${pageArea} (${pageRegistration.searchKeywords.join(", ")})` : pageArea,
        area: pageRegistration.pageArea.mainArea,
        name: label,
        resolvedUrl(urlResolver: UrlResolver) {
            return urlResolver.resolve(RouteLink());
        },
        isAuthorized() {
            return pageRegistration.requiredPermission === undefined || isAllowed(pageRegistration.requiredPermission);
        },
    };
}
const libraryPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.externalFeedsPage,
        RouteLink: () => links.externalFeedsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "External Feeds",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createFeedPage,
        RouteLink: () => links.createFeedPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Create Feed",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.stepTemplatesPage,
        RouteLink: () => links.stepTemplatesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Step Templates",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.selectBuiltInStepTemplatePage,
        RouteLink: () => links.selectBuiltInStepTemplatePage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Built-in Step Templates",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.communityStepTemplatesPage,
        RouteLink: () => links.communityStepTemplatesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Community Step Templates",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.lifecyclesPage,
        RouteLink: () => links.lifecyclesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Lifecycles",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createLifecyclePage,
        RouteLink: () => links.createLifecyclePage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Create Lifecycle",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.certificatesPage,
        RouteLink: () => links.certificatesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Certificates",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.addCertificatePage,
        RouteLink: () => links.addCertificatePage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Add Certificate",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.variableSetsPage,
        RouteLink: () => links.variableSetsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Variable Sets",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.builtInPackageRepositoryPage,
        RouteLink: () => links.builtInPackageRepositoryPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Built-in Package Repository",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.buildInformationRepositoryPage,
        RouteLink: () => links.buildInformationRepositoryPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Build Information",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.scriptModulesPage,
        RouteLink: () => links.scriptModulesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Script Modules",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createScriptModulePage,
        RouteLink: () => links.createScriptModulePage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Create Script Module",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.gitCredentialsPage,
        RouteLink: () => links.gitCredentialsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Git Credentials",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createGitCredentialPage,
        RouteLink: () => links.createGitCredentialPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Create Git Credential",
    }),
];
const configurationPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.inviteUsersPage,
        RouteLink: () => links.inviteUsersPage.generateUrl(),
        label: "Invite Users",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.auditPage,
        RouteLink: () => links.auditPage.generateUrl(),
        label: "Audit",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.auditArchivePage,
        RouteLink: () => links.auditArchivePage.generateUrl(),
        label: "Audit Archive",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.backupPage,
        RouteLink: () => links.backupPage.generateUrl(),
        label: "Backup",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.featuresPage,
        RouteLink: () => links.featuresPage.generateUrl(),
        label: "Features",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.licensePage,
        RouteLink: () => links.licensePage.generateUrl(),
        label: "License",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.licenseUsageDashboardPage,
        RouteLink: () => links.licenseUsageDashboardPage.generateUrl(),
        label: "License Usage Dashboard",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.smtpPage,
        RouteLink: () => links.smtpPage.generateUrl(),
        label: "SMTP",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.nodesPage,
        RouteLink: () => links.nodesPage.generateUrl(),
        label: "Nodes",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.serverSettingsPage,
        RouteLink: () => links.serverSettingsPage.generateUrl(),
        label: "Server Settings",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.thumbprintPage,
        RouteLink: () => links.thumbprintPage.generateUrl(),
        label: "Thumbprint",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.maintenancePage,
        RouteLink: () => links.maintenancePage.generateUrl(),
        label: "Maintenance",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.letsEncryptPage,
        RouteLink: () => links.letsEncryptPage.generateUrl(),
        label: "Let's Encrypt",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.letsEncryptSettingsPage,
        RouteLink: () => links.letsEncryptSettingsPage.generateUrl(),
        label: "Let's Encrypt Settings",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.testPermissionsPage,
        RouteLink: () => links.testPermissionsPage.generateUrl(),
        label: "Test Permission",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.apiKeysPage,
        RouteLink: () => links.apiKeysPage.generateUrl(),
        label: "API Keys",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.subscriptionsPage,
        RouteLink: () => links.subscriptionsPage.generateUrl(),
        label: "Subscriptions",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.createSubscriptionPage,
        RouteLink: () => links.createSubscriptionPage.generateUrl(),
        label: "Create Subscription",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.usersPage,
        RouteLink: () => links.usersPage.generateUrl(),
        label: "Users",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.createUserPage,
        RouteLink: () => links.createUserPage.generateUrl(),
        label: "Create User",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.spacesPage,
        RouteLink: () => links.spacesPage.generateUrl(),
        label: "Spaces",
    }),
    // Excluding creating spaces as an edge case - our UI creates these via a popover.
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.teamsPage,
        RouteLink: () => links.teamsPage.generateUrl(),
        label: "Teams",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.userRolesPage,
        RouteLink: () => links.userRolesPage.generateUrl(),
        label: "User Roles",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.createUserRolePage,
        RouteLink: () => links.createUserRolePage.generateUrl(),
        label: "Create User Role",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.diagnosticsPage,
        RouteLink: () => links.diagnosticsPage.generateUrl(),
        label: "Diagnostics",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.diagnosticServerLogsPage,
        RouteLink: () => links.diagnosticServerLogsPage.generateUrl(),
        label: "Diagnostic Server Logs",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.diagnosticServerTracesPage,
        RouteLink: () => links.diagnosticServerTracesPage.generateUrl(),
        label: "Diagnostic Server Traces",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.autoDeployLogsPage,
        RouteLink: () => links.autoDeployLogsPage.generateUrl(),
        label: "Auto Deploy Logs",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.scheduledTriggerLogsPage,
        RouteLink: () => links.scheduledTriggerLogsPage.generateUrl(),
        label: "Scheduled Trigger Logs",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.subscriptionLogsPage,
        RouteLink: () => links.subscriptionLogsPage.generateUrl(),
        label: "Subscription Logs",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.childGroups.settings.pages.settingsPage,
        RouteLink: () => links.settingsPage.generateUrl(),
        label: "Settings",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.performancePage,
        RouteLink: () => links.performancePage.generateUrl(),
        label: "Performance",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.telemetryPage,
        RouteLink: () => links.telemetryPage.generateUrl(),
        label: "Telemetry",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.deploymentFreezesPage,
        RouteLink: () => links.deploymentFreezesPage.generateUrl(),
        label: "Deployment Freezes",
    }),
];
const taskPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.pages.tasksPage,
        RouteLink: () => links.tasksPage.generateUrl(),
        label: "Tasks",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.pages.scriptConsolePage,
        RouteLink: () => links.scriptConsolePage.generateUrl(),
        label: "Script Console",
    }),
];
const projectTemplatePages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.platformHub.childGroups.projectTemplates.pages.projectTemplatesPage,
        RouteLink: () => links.projectTemplatesPage.generateUrl(),
        label: "Project Templates",
    }),
];
const policiesPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.platformHub.pages.policiesPage,
        RouteLink: () => links.policiesPage.generateUrl(),
        label: "Policies",
    }),
];
const processTemplatesPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.platformHub.pages.processTemplatesPage,
        RouteLink: () => links.processTemplatesPage.generateUrl(),
        label: "Process Templates",
    }),
];
const basePlatformHubPages: () => GlobalSearchPageRegistration[] = () => {
    const results: GlobalSearchPageRegistration[] = [
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.system.childGroups.platformHub.pages.platformHubVersionControlSettingsPage,
            RouteLink: () => links.platformHubVersionControlSettingsPage.generateUrl(),
            label: "Version Control Settings",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.system.childGroups.platformHub.pages.platformHubAccountsPage,
            RouteLink: () => links.platformHubAccountsPage.generateUrl(),
            label: "Accounts",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.system.childGroups.platformHub.pages.platformHubGitCredentialsPage,
            RouteLink: () => links.platformHubGitCredentialsPage.generateUrl(),
            label: "Git Credentials",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.system.childGroups.platformHub.pages.platformHubCreateGitCredentialPage,
            RouteLink: () => links.platformHubCreateGitCredentialPage.generateUrl(),
            label: "Create Git Credential",
        }),
    ];
    const accountTypeDetailsMap = RawAccountTypeDetailsMap;
    accountTypeDetailsMap.forEach((accountTypeDetails) => {
        if (accountTypeDetails.types.length === 0) {
            return null;
        }
        results.push(createGlobalSearchPageRegistration({
            label: `Add New ${accountTypeDetails.name} ${accountTypeDetails.name.endsWith("Account") ? "" : "Account"}`,
            pageRegistration: allPages.childGroups.system.childGroups.platformHub.pages.platformHubAddAccountPage,
            RouteLink: () => links.platformHubAddAccountPage.generateUrl({ accountType: accountTypeDetails.types[0] }),
        }));
    });
    return results;
};
const infrastructurePages: () => GlobalSearchPageRegistration[] = () => {
    const results: GlobalSearchPageRegistration[] = [
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.infrastructureOverviewPage,
            RouteLink: () => links.infrastructureOverviewPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Infrastructure Overview",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.environmentsPage,
            RouteLink: () => links.environmentsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Environments",
        }),
        // Excluding creating environments as an edge case - our UI creates these via a popover.
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.deploymentTargetsPage,
            RouteLink: () => links.deploymentTargetsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Deployment Targets",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.selectDeploymentTargetTypePage,
            RouteLink: () => links.selectDeploymentTargetTypePage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Add Deployment Target",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.machineProxiesPage,
            RouteLink: () => links.machineProxiesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Machine Proxies",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.addMachineProxyPage,
            RouteLink: () => links.addMachineProxyPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Add Machine Proxy",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.machinePoliciesPage,
            RouteLink: () => links.machinePoliciesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Machine Policies",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.createMachinePolicyPage,
            RouteLink: () => links.createMachinePolicyPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Create Machine Policy",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.accountsPage,
            RouteLink: () => links.accountsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Accounts",
        }),
        // Excluding creating accounts as an edge case - our UI creates these via a popover.
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.workersPage,
            RouteLink: () => links.workersPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Workers",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.selectWorkerTypePage,
            RouteLink: () => links.selectWorkerTypePage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Add New Worker",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.workerPoolsPage,
            RouteLink: () => links.workerPoolsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Worker Pools",
        }),
        // Excluding creating worker pools as an edge case - our UI creates these via a popover.
    ];
    // Inject our account types dynamically.
    const accountTypeDetailsMap = RawAccountTypeDetailsMap;
    accountTypeDetailsMap.forEach((accountTypeDetails) => {
        if (accountTypeDetails.types.length === 0) {
            return null;
        }
        results.push(createGlobalSearchPageRegistration({
            label: `Add New ${accountTypeDetails.name} ${accountTypeDetails.name.endsWith("Account") ? "" : "Account"}`,
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.addAccountPage,
            RouteLink: () => links.addAccountPage.generateUrl({ spaceId: client.spaceId ?? "" }, { accountType: accountTypeDetails.types[0] }),
        }));
    });
    return results;
};
const projectPages = (): GlobalSearchPageRegistration[] => {
    return [
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.projects.pages.projectsPage,
            RouteLink: () => links.projectsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Projects",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.projects.childGroups.importExports.pages.importExportTasksPage,
            RouteLink: () => links.importExportTasksPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Import/Export Tasks",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.projects.childGroups.importExports.pages.importProjectsPage,
            RouteLink: () => links.importProjectsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Import Projects",
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.projects.childGroups.importExports.pages.exportProjectsPage,
            RouteLink: () => links.exportProjectsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
            label: "Export Projects",
        }),
    ];
};
const tenantPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.tenants.pages.tenantsPage,
        RouteLink: () => links.tenantsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Tenants",
    }),
];
const tagSetPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.tagSetsPage,
        RouteLink: () => links.tagSetsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Tenant Tag Sets",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createTagSetPage,
        RouteLink: () => links.createTagSetPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        label: "Create Tenant Tag Set",
    }),
];
const myProfilePages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.myProfile.pages.myLoginsPage,
        RouteLink: () => links.myLoginsPage.generateUrl(),
        label: "My Logins",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.myProfile.pages.myProfilePage,
        RouteLink: () => links.myProfilePage.generateUrl(),
        label: "My Profile",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.myProfile.pages.myApiKeysPage,
        RouteLink: () => links.myApiKeysPage.generateUrl(),
        label: "My API Keys",
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.myProfile.pages.myRecentActivityPage,
        RouteLink: () => links.myRecentActivityPage.generateUrl(),
        label: "My Recent Activity",
    }),
];
export const pagesToIncludeInSearch = () => {
    const platformHubPages: GlobalSearchPageRegistration[] = [...basePlatformHubPages(), ...processTemplatesPages, ...(isFeatureToggleEnabled("project-templates") ? projectTemplatePages : []), ...policiesPages];
    return [...libraryPages, ...configurationPages, ...taskPages, ...infrastructurePages(), ...projectPages(), ...tenantPages, ...myProfilePages, ...tagSetPages, ...platformHubPages];
};
function getPageArea({ mainArea, subArea }: {
    mainArea: string;
    subArea?: string;
}): string {
    const delimiter = subArea ? " / " : "";
    return `${mainArea}${delimiter}${subArea ?? ""}`;
}
